import React, { useState, useEffect } from "react";
import { createSimpleProductAPI } from "../../../../api/index";
import "./SimpleProductRegisterModal.css";

const MA_ID_GENERAL = 1;
const MA_ID_ORIGIN = 2;

const REGISTER_MODE = {
  GENERAL: "general",
  ORIGIN: "origin",
};

/** 쿼리·해시 제거한 상품 URL (스마트스토어 등) */
export const normalizeSmartStoreUrl = (raw) => {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.startsWith("@")) s = s.slice(1).trim();
  try {
    const u = new URL(s);
    u.search = "";
    u.hash = "";
    let out = u.toString();
    if (out.endsWith("/")) out = out.slice(0, -1);
    return out;
  } catch {
    return s.split("?")[0].split("#")[0].trim().replace(/\/$/, "");
  }
};

export const extractNaverProductIdFromUrl = (urlStr) => {
  const normalized = normalizeSmartStoreUrl(urlStr);
  const m = normalized.match(/\/products\/(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
};

const parseKeywordsFromInput = (text) =>
  String(text ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const SimpleProductRegisterModal = ({
  isOpen,
  onClose,
  onSuccess,
  atProductLimit,
  atKeywordLimit = false,
  keywordCount = 0,
  keywordLimit = 999,
}) => {
  const [registerMode, setRegisterMode] = useState(REGISTER_MODE.GENERAL);
  const [productUrl, setProductUrl] = useState("");
  const [originProductUrl, setOriginProductUrl] = useState("");
  const [keywordsText, setKeywordsText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRegisterMode(REGISTER_MODE.GENERAL);
    setProductUrl("");
    setOriginProductUrl("");
    setKeywordsText("");
    setSubmitting(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const cannotRegister = atProductLimit || atKeywordLimit;

  const handleSubmit = async () => {
    if (atKeywordLimit) {
      window.alert(
        "등록 가능한 키워드 수를 모두 사용했습니다. 키워드 한도가 찼을 때는 상품을 추가할 수 없습니다.",
      );
      return;
    }
    if (atProductLimit) {
      window.alert("등록 가능한 상품 수를 모두 사용했습니다.");
      return;
    }

    const normalizedMain = normalizeSmartStoreUrl(productUrl);
    if (!normalizedMain) {
      window.alert("상품 URL 주소를 입력해주세요.");
      return;
    }

    const keywords = parseKeywordsFromInput(keywordsText);
    if (!keywords.length) {
      window.alert("키워드를 쉼표(,)로 구분하여 입력해주세요.");
      return;
    }

    const limitKw = Math.max(0, Number(keywordLimit) || 0);
    const curKw = Math.max(0, Number(keywordCount) || 0);
    const remainingKw = Math.max(0, limitKw - curKw);
    if (keywords.length > remainingKw) {
      window.alert(
        limitKw === 0
          ? "키워드 한도를 확인할 수 없거나 등록할 수 없습니다."
          : `키워드는 총 ${limitKw}개까지만 등록할 수 있습니다. (현재 ${curKw}개 사용 중 → 최대 ${remainingKw}개까지 추가 가능, 이번에 ${keywords.length}개 입력)`,
      );
      return;
    }

    let payload;

    if (registerMode === REGISTER_MODE.GENERAL) {
      const productId = extractNaverProductIdFromUrl(normalizedMain);
      if (productId == null) {
        window.alert(
          "상품 URL에서 상품 번호를 찾을 수 없습니다. …/products/숫자 형식인지 확인해주세요.",
        );
        return;
      }
      payload = {
        productUrl: normalizedMain,
        ma_id: MA_ID_GENERAL,
        productId,
        keywords,
      };
    } else {
      const normalizedOrigin = normalizeSmartStoreUrl(originProductUrl);
      if (!normalizedOrigin) {
        window.alert("카테고리(원부) 상품 URL을 입력해주세요.");
        return;
      }
      const productId = extractNaverProductIdFromUrl(normalizedOrigin);
      if (productId == null) {
        window.alert(
          "카테고리 상품 URL에서 상품 번호를 찾을 수 없습니다. …/products/숫자 형식인지 확인해주세요.",
        );
        return;
      }
      payload = {
        productUrl: normalizedMain,
        origin_product_url: normalizedOrigin,
        ma_id: MA_ID_ORIGIN,
        productId,
        keywords,
      };
    }

    setSubmitting(true);
    try {
      await createSimpleProductAPI(payload);
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error("간편 상품 등록 실패:", err);
      window.alert(
        err.response?.data?.msg ||
          err.message ||
          "상품 등록에 실패했습니다.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rk-reg-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="rk-reg-popup"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rk-reg-title"
      >
        <div className="rk-reg-header">
          <h2 id="rk-reg-title">상품 등록</h2>
          <button
            type="button"
            className="rk-reg-close"
            onClick={onClose}
            aria-label="닫기"
          />
        </div>

        <div className="rk-reg-body">
          {atKeywordLimit && (
            <div className="rk-reg-banner">
              키워드 한도에 도달했습니다. 새 상품은 추가할 수 없습니다.
            </div>
          )}
          {atProductLimit && (
            <div className="rk-reg-banner">
              등록 가능한 상품 수를 모두 사용했습니다.
            </div>
          )}

          <div className="rk-reg-mode" role="group" aria-label="등록 유형">
            <button
              type="button"
              className={
                registerMode === REGISTER_MODE.GENERAL
                  ? "rk-reg-mode-btn active"
                  : "rk-reg-mode-btn"
              }
              onClick={() => setRegisterMode(REGISTER_MODE.GENERAL)}
              disabled={submitting || cannotRegister}
            >
              일반
            </button>
            <button
              type="button"
              className={
                registerMode === REGISTER_MODE.ORIGIN
                  ? "rk-reg-mode-btn active"
                  : "rk-reg-mode-btn"
              }
              onClick={() => setRegisterMode(REGISTER_MODE.ORIGIN)}
              disabled={submitting || cannotRegister}
            >
              원부
            </button>
          </div>

          <div className="rk-reg-field">
            <label htmlFor="rk-reg-product-url">상품 URL 주소</label>
            <input
              id="rk-reg-product-url"
              type="url"
              className="rk-reg-input"
              placeholder="https://smartstore.naver.com/…/products/…"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              onBlur={() =>
                setProductUrl((v) => normalizeSmartStoreUrl(v))
              }
              disabled={submitting || cannotRegister}
            />
            <p className="rk-reg-hint">
              포커스를 벗어나면 주소에서 쿼리(?) 뒤가 자동으로 제거됩니다.
            </p>
          </div>

          {registerMode === REGISTER_MODE.ORIGIN && (
            <div className="rk-reg-field">
              <label htmlFor="rk-reg-origin-url">카테고리 상품 URL</label>
              <input
                id="rk-reg-origin-url"
                type="url"
                className="rk-reg-input"
                placeholder="원부(카테고리) 상품 페이지 URL"
                value={originProductUrl}
                onChange={(e) => setOriginProductUrl(e.target.value)}
                onBlur={() =>
                  setOriginProductUrl((v) => normalizeSmartStoreUrl(v))
                }
                disabled={submitting || cannotRegister}
              />
              <p className="rk-reg-hint">
                등록 시 상품 번호(productId)는 이 URL에서 추출합니다.
              </p>
            </div>
          )}

          <div className="rk-reg-field">
            <label htmlFor="rk-reg-keywords">키워드</label>
            <textarea
              id="rk-reg-keywords"
              className="rk-reg-textarea"
              rows={3}
              placeholder="노즈워크, 딸기, 장난감 (쉼표로 구분)"
              value={keywordsText}
              onChange={(e) => setKeywordsText(e.target.value)}
              disabled={submitting || cannotRegister}
            />
          </div>

          <div className="rk-reg-actions">
            <button
              type="button"
              className="rk-reg-btn secondary"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </button>
            <button
              type="button"
              className="rk-reg-btn primary"
              onClick={handleSubmit}
              disabled={submitting || cannotRegister}
            >
              {submitting ? "등록 중…" : "등록"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleProductRegisterModal;
